import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';

const emptyAsset = { name: '', code: '', category: '', unit: 'adet', stock: 0, location: '', description: '' };
const STATUS_LABELS = { pending: 'Bekliyor', approved: 'Onaylandı', rejected: 'Reddedildi', returned: 'İade edildi' };

const AssetsPage = () => {
  const { toasts, showToast, removeToast } = useToast();
  const [tab, setTab] = useState('catalog');
  const [assets, setAssets] = useState([]);
  const [requests, setRequests] = useState([]);
  const [report, setReport] = useState(null);
  const [access, setAccess] = useState({ manageCatalog: false, manageRequests: false });
  const [form, setForm] = useState(emptyAsset);
  const [requestForm, setRequestForm] = useState({ assetId: '', quantity: 1, note: '' });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [assetResponse, requestResponse] = await Promise.all([
        api.getAssets({ includeInactive: 'true' }),
        api.getAssetRequests()
      ]);
      setAssets(assetResponse.data || []);
      setAccess(assetResponse.access || {});
      setRequests(requestResponse.data || []);
      if (assetResponse.access?.manageCatalog) {
        const reportResponse = await api.getAssetReport();
        setReport(reportResponse.data || null);
      }
    } catch (error) {
      showToast(error.message || 'Demirbaş verileri alınamadı.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const saveAsset = async (event) => {
    event.preventDefault();
    try {
      await api.createAsset({ ...form, stock: Number(form.stock) });
      setForm(emptyAsset);
      showToast('Demirbaş eklendi.', 'success');
      await load();
    } catch (error) {
      showToast(error.message || 'Demirbaş eklenemedi.', 'error');
    }
  };

  const adjustStock = async (asset, nextStock) => {
    try {
      await api.updateAsset(asset._id, { stock: nextStock, isActive: asset.isActive });
      await load();
    } catch (error) {
      showToast(error.message || 'Stok güncellenemedi.', 'error');
    }
  };

  const submitRequest = async (event) => {
    event.preventDefault();
    try {
      await api.createAssetRequest({ ...requestForm, quantity: Number(requestForm.quantity) });
      setRequestForm({ assetId: '', quantity: 1, note: '' });
      showToast('Talebiniz iletildi.', 'success');
      await load();
      setTab('requests');
    } catch (error) {
      showToast(error.message || 'Talep oluşturulamadı.', 'error');
    }
  };

  const decide = async (request, status) => {
    try {
      await api.decideAssetRequest(request._id, { status });
      showToast('Talep güncellendi.', 'success');
      await load();
    } catch (error) {
      showToast(error.message || 'Talep güncellenemedi.', 'error');
    }
  };

  const stockedAssets = assets.filter((asset) => asset.isActive && asset.stock > 0);

  return (
    <div className="documents-page modern-hr-page">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="modern-hr-header mb-4">
        <h1>Demirbaş Yönetimi</h1>
        <p>Stoklu ürünleri izleyin, talep edin ve zimmetlenen demirbaşları takip edin.</p>
        <div className="corporate-category-filters">
          <button className={`corporate-filter-chip ${tab === 'catalog' ? 'active' : ''}`} onClick={() => setTab('catalog')}>Katalog</button>
          <button className={`corporate-filter-chip ${tab === 'request' ? 'active' : ''}`} onClick={() => setTab('request')}>Talep et</button>
          <button className={`corporate-filter-chip ${tab === 'requests' ? 'active' : ''}`} onClick={() => setTab('requests')}>Talepler</button>
          {access.manageCatalog && <button className={`corporate-filter-chip ${tab === 'track' ? 'active' : ''}`} onClick={() => setTab('track')}>Takip</button>}
        </div>
      </div>

      {loading ? <div className="modern-loading"><div className="spinner-border" /></div> : (
        <>
          {tab === 'catalog' && (
            <div className="modern-card p-4">
              {access.manageCatalog && (
                <form className="row g-3 mb-4" onSubmit={saveAsset}>
                  <div className="col-md-3"><input className="form-control modern-input" placeholder="Ürün adı" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required /></div>
                  <div className="col-md-2"><input className="form-control modern-input" placeholder="Kod" value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value })} required /></div>
                  <div className="col-md-2"><input className="form-control modern-input" placeholder="Kategori" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} required /></div>
                  <div className="col-md-1"><input type="number" min="0" className="form-control modern-input" value={form.stock} onChange={(event) => setForm({ ...form, stock: event.target.value })} required /></div>
                  <div className="col-md-2"><input className="form-control modern-input" placeholder="Lokasyon" value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} /></div>
                  <div className="col-md-2"><button className="btn btn-modern-primary w-100" type="submit">Ekle</button></div>
                </form>
              )}
              <div className="table-responsive">
                <table className="table modern-table">
                  <thead><tr><th>Kod</th><th>Ürün</th><th>Kategori</th><th>Stok</th><th>Lokasyon</th>{access.manageCatalog && <th>Stok düzelt</th>}</tr></thead>
                  <tbody>
                    {assets.map((asset) => (
                      <tr key={asset._id}>
                        <td>{asset.code}</td>
                        <td>{asset.name}</td>
                        <td>{asset.category}</td>
                        <td>{asset.stock} {asset.unit}</td>
                        <td>{asset.location || '-'}</td>
                        {access.manageCatalog && (
                          <td>
                            <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => adjustStock(asset, asset.stock + 1)}>+</button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => adjustStock(asset, Math.max(0, asset.stock - 1))}>-</button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {!assets.length && <tr><td colSpan="6">Henüz demirbaş yok.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'request' && (
            <form className="modern-card p-4" onSubmit={submitRequest}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="modern-form-label">Ürün</label>
                  <select className="form-select modern-input" value={requestForm.assetId} onChange={(event) => setRequestForm({ ...requestForm, assetId: event.target.value })} required>
                    <option value="">Stoktaki ürünü seçin</option>
                    {stockedAssets.map((asset) => <option key={asset._id} value={asset._id}>{asset.name} ({asset.stock} {asset.unit})</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="modern-form-label">Adet</label>
                  <input type="number" min="1" className="form-control modern-input" value={requestForm.quantity} onChange={(event) => setRequestForm({ ...requestForm, quantity: event.target.value })} required />
                </div>
                <div className="col-md-12">
                  <label className="modern-form-label">Not</label>
                  <textarea className="form-control modern-input" rows="3" value={requestForm.note} onChange={(event) => setRequestForm({ ...requestForm, note: event.target.value })} />
                </div>
              </div>
              <button className="btn btn-modern-primary mt-3" type="submit" disabled={!stockedAssets.length}>Talep oluştur</button>
            </form>
          )}

          {tab === 'requests' && (
            <div className="modern-card p-4">
              <div className="table-responsive">
                <table className="table modern-table">
                  <thead><tr><th>Ürün</th><th>Talep eden</th><th>Adet</th><th>Durum</th><th>Not</th>{access.manageRequests && <th>İşlem</th>}</tr></thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr key={request._id}>
                        <td>{request.asset?.name || '-'}</td>
                        <td>{request.requester ? `${request.requester.firstName} ${request.requester.lastName}` : '-'}</td>
                        <td>{request.quantity}</td>
                        <td>{STATUS_LABELS[request.status] || request.status}</td>
                        <td>{request.note || '-'}</td>
                        {access.manageRequests && (
                          <td>
                            {request.status === 'pending' && (
                              <>
                                <button className="btn btn-sm btn-outline-success me-1" onClick={() => decide(request, 'approved')}>Onayla</button>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => decide(request, 'rejected')}>Reddet</button>
                              </>
                            )}
                            {request.status === 'approved' && <button className="btn btn-sm btn-outline-secondary" onClick={() => decide(request, 'returned')}>İade al</button>}
                          </td>
                        )}
                      </tr>
                    ))}
                    {!requests.length && <tr><td colSpan="6">Talep bulunmuyor.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'track' && report && (
            <div className="row g-4">
              <div className="col-md-4"><div className="modern-card p-4"><h6>Ürün</h6><strong>{report.summary.productCount}</strong></div></div>
              <div className="col-md-4"><div className="modern-card p-4"><h6>Toplam stok</h6><strong>{report.summary.stockTotal}</strong></div></div>
              <div className="col-md-4"><div className="modern-card p-4"><h6>Aktif zimmet</h6><strong>{report.summary.assignedCount}</strong></div></div>
              <div className="col-12">
                <div className="modern-card p-4">
                  <h5 className="fw-bold mb-3">Kimde ne var</h5>
                  <table className="table modern-table">
                    <thead><tr><th>Ürün</th><th>Çalışan</th><th>Şirket</th><th>Adet</th><th>Çalışan no</th></tr></thead>
                    <tbody>
                      {(report.assignments || []).map((item) => (
                        <tr key={item._id}>
                          <td>{item.asset?.name}</td>
                          <td>{item.requester ? `${item.requester.firstName} ${item.requester.lastName}` : '-'}</td>
                          <td>{item.requester?.company || '-'}</td>
                          <td>{item.quantity}</td>
                          <td>{item.requester?.employeeNumber || '-'}</td>
                        </tr>
                      ))}
                      {!report.assignments?.length && <tr><td colSpan="5">Aktif zimmet yok.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AssetsPage;
